import React from 'react';
import type { ItemLayout } from '../schema';
import { cn } from '../lib/utils';
import { toCssSize } from './responsive';

// ============================================
// Layout Item Props
// ============================================

export interface LayoutItemProps {
    /** Item ID */
    id: string;
    /** Children to render */
    children: React.ReactNode;
    /** Layout configuration for this item */
    layout?: ItemLayout;
    /** Additional CSS class */
    className?: string;
    /** Additional inline styles */
    style?: React.CSSProperties;
    /** Click handler */
    onClick?: () => void;
    /** Whether the item is focused/active */
    isActive?: boolean;
    /** Whether the item is disabled */
    isDisabled?: boolean;
}

// ============================================
// Layout Item Component
// ============================================

/**
 * Wrapper component for individual content items in a layout
 */
export function LayoutItem({
    id,
    children,
    layout,
    className,
    style,
    onClick,
    isActive = false,
    isDisabled = false,
}: LayoutItemProps): React.ReactElement {
    // Build styles from layout config
    const itemStyles: React.CSSProperties = {
        ...style,
        ...layout?.style,
    };

    // Apply layout properties
    if (layout) {
        // Grid positioning
        if (layout.colSpan) {
            itemStyles.gridColumn = `span ${layout.colSpan}`;
        }
        if (layout.rowSpan) {
            itemStyles.gridRow = `span ${layout.rowSpan}`;
        }
        if (layout.colStart) {
            itemStyles.gridColumnStart = layout.colStart;
        }
        if (layout.rowStart) {
            itemStyles.gridRowStart = layout.rowStart;
        }

        // Sizing
        if (layout.width !== undefined) {
            itemStyles.width = toCssSize(layout.width);
        }
        if (layout.height !== undefined) {
            itemStyles.height = toCssSize(layout.height);
        }
        if (layout.minWidth !== undefined) {
            itemStyles.minWidth = toCssSize(layout.minWidth);
        }
        if (layout.minHeight !== undefined) {
            itemStyles.minHeight = toCssSize(layout.minHeight);
        }
        if (layout.maxWidth !== undefined) {
            itemStyles.maxWidth = toCssSize(layout.maxWidth);
        }
        if (layout.maxHeight !== undefined) {
            itemStyles.maxHeight = toCssSize(layout.maxHeight);
        }

        // Order
        if (layout.order !== undefined) {
            itemStyles.order = layout.order;
        }

        // Alignment
        if (layout.alignment) {
            itemStyles.alignSelf = mapAlignmentToFlexAlign(layout.alignment);
        }
    }

    return (
        <div
            id={`layout-item-${id}`}
            data-layout-item={id}
            className={cn(
                'layout-item',
                isActive && 'layout-item--active',
                isDisabled && 'layout-item--disabled opacity-50 pointer-events-none',
                layout?.className,
                className
            )}
            style={itemStyles}
            onClick={isDisabled ? undefined : onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick && !isDisabled ? 0 : undefined}
            onKeyDown={
                onClick && !isDisabled
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
        >
            {children}
        </div>
    );
}

// ============================================
// Utilities
// ============================================

/**
 * Map layout alignment to flexbox align-self value
 */
function mapAlignmentToFlexAlign(
    alignment: ItemLayout['alignment']
): React.CSSProperties['alignSelf'] {
    switch (alignment) {
        case 'start':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'end':
            return 'flex-end';
        case 'stretch':
            return 'stretch';
        default:
            return undefined;
    }
}

/**
 * Get default item layout
 */
export function getDefaultItemLayout(itemId: string): ItemLayout {
    return {
        itemId,
    };
}

/**
 * Merge item layouts
 */
export function mergeItemLayouts(
    base: ItemLayout,
    override?: Partial<ItemLayout>
): ItemLayout {
    if (!override) return base;
    return { ...base, ...override };
}
